import { useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Check, ChevronRight, Download, Upload } from "lucide-react"
import { toast } from "sonner"
import type { ContactImport } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Separator } from "@/components/ui/separator"
import { Header, Hint, Section } from "@/components/audience/primitives"
import { downloadCsv } from "@/lib/download"
import { api, ApiError } from "@/lib/api"
import { workspaceQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/contacts/import/")({
  beforeLoad: async ({ context, params }) => {
    const ctx = await context.queryClient.ensureQueryData(workspaceQuery(params.workspace))
    if (!ctx.permissions.includes("manage_customer_data")) throw redirect({ to: "/w/$workspace/contacts", params, search: {} })
  },
  component: ImportUpload,
})

const TEMPLATE = [
  ["display_name", "email", "instagram_handle", "city", "tags", "notes"],
  ["Riley Quinn", "riley@example.test", "@riley.draws", "Hackney", "Studio community", "Met at the September open day"],
]

/** Screen 32, step 1: choose the file. Mapping happens on the next screen once rows are stored. */
function ImportUpload() {
  const { workspace } = useWorkspaceContext()
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const params = { workspace: workspace.slug }
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text()
      return api<ContactImport>(`/w/${workspace.slug}/contacts/imports?fileName=${encodeURIComponent(file.name)}`, { method: "POST", body: text, headers: { "content-type": "text/csv" } })
    },
    onSuccess: (created) => navigate({ to: "/w/$workspace/contacts/import/$importId", params: { ...params, importId: created.id } }),
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not read the file"),
  })
  function choose(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") return toast.error("Choose a CSV file")
    upload.mutate(file)
  }
  return (
    <div className="page-wrap au-root">
      <Header title="Import contacts" description="Bring your contact details together, with a review before import." back={<Link to="/w/$workspace/contacts" params={params} search={{}}><ArrowLeft /> Contacts</Link>}>
        <Button variant="outline" onClick={() => downloadCsv("vienna-contacts-template.csv", TEMPLATE)}>
          <Download /> Download template
        </Button>
      </Header>
      <div className="au-import-steps">
        <strong>1. Upload file</strong>
        <ChevronRight className="size-3.5" />
        <span>2. Map columns</span>
        <ChevronRight className="size-3.5" />
        <span>3. Review and import</span>
      </div>
      <div className="au-two-column">
        <Section title="Upload a CSV file" description="Up to 5,000 rows per file. The first row holds the column names.">
          <div
            className={`rounded-xl border border-dashed p-12 text-center ${dragging ? "bg-muted" : ""}`}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              choose(e.dataTransfer.files)
            }}
          >
            <Upload className="mx-auto mb-3 size-6 text-muted-foreground" />
            <p className="font-medium">Drop a CSV here</p>
            <p className="mt-1 mb-5 text-sm text-muted-foreground">or choose a file from your computer.</p>
            <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => choose(e.target.files)} />
            <Button onClick={() => input.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? "Reading…" : "Choose file"}
            </Button>
          </div>
        </Section>
        <Section title="Import behavior">
          <div className="space-y-5">
            {[
              ["Create new person records", "For rows without an exact match."],
              ["Review exact email matches", "Decide on the next screen whether matches are updated."],
              ["Preserve existing permission", "Imports never replace consent records."],
            ].map(([title, detail], i) => (
              <label className="flex items-start gap-3" key={title}>
                <Checkbox checked disabled={i !== 1} />
                <span>
                  <span className="block text-sm font-medium">{title}</span>
                  <span className="text-xs text-muted-foreground">{detail}</span>
                </span>
              </label>
            ))}
          </div>
          <Separator className="my-5" />
          <Hint>A “yes” value without source evidence doesn’t grant marketing permission. New profiles remain unknown.</Hint>
          <p className="mt-4 text-xs leading-5 text-muted-foreground">Imported handles are reference data. They don’t verify a channel identity or open a messaging window.</p>
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="size-3.5" /> Every import is recorded in the audit log.
          </p>
        </Section>
      </div>
    </div>
  )
}
